import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { IntegrationServices } from '@sequencehq/api/utils/commonEnums'
import { getIntegrationName } from 'Integrations/utils/getIntegrationName'
import { useNotifications } from '@sequencehq/core-components'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  dashboardv99990101Client,
  type Dashboardv99990101CommonModels
} from '@sequencehq/api/dashboard/v99990101'

interface LoadedData {
  customer: Dashboardv99990101CommonModels.Customers.Customer | null
}

enum Status {
  UNINITIALIZED = 'UNINITIALIZED',
  LOADING = 'LOADING',
  UNLINKING = 'UNLINKING',
  LOADED = 'LOADED'
}

type UseUnlinkCustomerModal = (props: {
  customerId: string
  service: IntegrationServices
  onConfirm?: () => void
}) => {
  loaded: boolean
  customerName: string
  serviceName: string
  submit: {
    onClick: () => Promise<boolean>
    disabled: boolean
    unlinking: boolean
  }
}

/**
 * This hook powers the unlink customer modal. The modal itself is designed to
 * be used in any situation, and therefore packs its own data loading capabilities.
 * As of the point of implementation, we expect to be using this modal in
 * the context of the 'Inspector' present in Quote and Customer screens as well
 * as a kebab menu item in the customer list.
 *
 * @param props
 * @returns
 */
export const useUnlinkCustomerModal: UseUnlinkCustomerModal = props => {
  const { displayNotification } = useNotifications()
  const [status, setStatus] = useState<Status>(Status.UNINITIALIZED)
  const [loadedData, setLoadedData] = useState<LoadedData>({
    customer: null
  })

  const serviceName = useMemo(() => {
    return getIntegrationName(props.service)
  }, [props.service])
  /**
   * A simple load function which will grab the customer and the integration
   * customers for the given service.
   */
  const load = useCallback(async ({ customerId }: { customerId: string }) => {
    setStatus(Status.LOADING)

    const customer = await dashboardv99990101Client.getCustomer({
      id: customerId
    })

    setLoadedData({
      customer: customer.data ?? null
    })
    setStatus(Status.LOADED)
  }, [])

  const unlinkCustomer = useCallback(
    async ({
      service,
      customerId
    }: {
      service: IntegrationServices
      customerId: string
    }) => {
      setStatus(Status.UNLINKING)

      const res = await dashboard20240730Client.deleteLinkedEntity({
        type: 'CUSTOMER',
        id: customerId,
        service
      })

      setStatus(Status.LOADED)
      if (res.error) {
        displayNotification(`Could not unlink customer from ${serviceName}`, {
          type: 'error'
        })
        return false
      }

      displayNotification(`Customer unlinked from ${serviceName}`, {
        type: 'success'
      })
      return true
    },
    [displayNotification, serviceName]
  )

  useEffect(() => {
    void load({
      customerId: props.customerId
    })
  }, [props.customerId, load])

  const submit = useMemo(() => {
    return {
      onClick: async (): Promise<boolean> => {
        const success = await unlinkCustomer({
          service: props.service,
          customerId: props.customerId
        })
        if (success) {
          props.onConfirm?.()
        }
        return success
      },
      disabled: status === Status.UNLINKING,
      unlinking: status === Status.UNLINKING
    }
  }, [status, unlinkCustomer, props.onConfirm, props.customerId, props.service])

  return {
    loaded: ![Status.LOADING, Status.LOADING].includes(status),
    customerName: loadedData.customer?.legalName ?? '',
    serviceName,
    submit
  }
}
